import React from 'react';
import { useNavigate } from 'react-router-dom';
import MetricCard from './MetricCard';

export const ReceptionCommandCenter = ({ stats, loading, activeCentreName }) => {
  const navigate = useNavigate();

  const phlebo = stats?.phlebotomy || {};
  const settle = stats?.settlement || {};
  const recent = stats?.recentRegistrations?.items || [];
  const totalPatients = phlebo.totalPatientsToday ?? stats?.recentRegistrations?.totalCount ?? 0;
  const pendingCollection = phlebo.pendingCount ?? 0;
  const unpaidCount = settle.unpaidCount ?? 0;
  const totalDue = settle.totalDueAmount ?? 0;

  return (
    <div className="lis-command-deck reception-deck">
      {/* 1. Reception Summary & Operational Queue */}
      <div className="lis-deck-section">
        <div className="lis-section-header-compact">
          <div className="lis-section-header-left">
            <span className="lis-section-kicker">FRONT DESK KPIS</span>
            <h3 className="lis-section-title">Intake & Daily Patient Volume</h3>
            <span className="lis-section-sub">&bull; {activeCentreName}</span>
          </div>
          <div className="lis-deck-actions">
            <button
              type="button"
              className="lis-deck-action-primary"
              onClick={() => navigate('/registration')}
            >
              + New Patient
            </button>
            <button
              type="button"
              className="lis-deck-quick-btn"
              onClick={() => navigate('/settlement')}
            >
              Collect Due
            </button>
          </div>
        </div>

        <div className="lis-kpi-grid">
          <MetricCard
            title="Today's Registered Patients"
            value={totalPatients}
            subtitle="Intake check-ins"
            status={loading ? 'LOADING' : 'LIVE'}
            route="/registration"
            variant="info"
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M19 8v6m3-3h-6" />
              </svg>
            }
          />

          <MetricCard
            title="Waiting for Phlebotomy"
            value={pendingCollection}
            subtitle="Direct to sample collection"
            status={loading ? 'LOADING' : 'LIVE'}
            route="/phlebotomy"
            variant={pendingCollection > 0 ? 'warning' : 'success'}
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            }
          />

          <MetricCard
            title="Unpaid Patient Bills"
            value={unpaidCount}
            subtitle={`Total outstanding: ₹${Number(totalDue).toLocaleString('en-IN')}`}
            status={loading ? 'LOADING' : 'LIVE'}
            route="/settlement"
            variant={unpaidCount > 0 ? 'purple' : 'success'}
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            }
          />

          <MetricCard
            title="Cancelled Orders"
            value={0}
            subtitle="No cancellations logged"
            status="NO DATA"
            route="/settlement"
            variant="default"
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            }
          />
        </div>
      </div>

      {/* 2. My Work & Front-Desk Action Cards */}
      <div className="lis-deck-section">
        <h4 className="lis-sub-section-title">Front Desk Operational Worklist</h4>
        <div className="lis-work-tasks-grid">
          <div className="lis-task-card" onClick={() => navigate('/registration')}>
            <div className="lis-task-badge pending">ACTION REQUIRED</div>
            <h5 className="lis-task-title">Patient Intake & Order Booking</h5>
            <p className="lis-task-desc">Enter demographic information, prescribe investigation panels, and generate barcodes.</p>
            <span className="lis-task-link">Open Registration &rarr;</span>
          </div>

          <div className="lis-task-card" onClick={() => navigate('/settlement')}>
            <div className={`lis-task-badge ${unpaidCount > 0 ? 'warning' : 'success'}`}>
              {unpaidCount > 0 ? `${unpaidCount} BILLS PENDING` : 'ALL CLEARED'}
            </div>
            <h5 className="lis-task-title">Fee Collection & Receipt Settlement</h5>
            <p className="lis-task-desc">Collect cash, card, UPI payments, issue official receipts, or record credit balances.</p>
            <span className="lis-task-link">Open Billing Counter &rarr;</span>
          </div>

          <div className="lis-task-card" onClick={() => navigate('/phlebotomy')}>
            <div className={`lis-task-badge ${pendingCollection > 0 ? 'info' : 'success'}`}>
              {pendingCollection} WAITING FOR PHLEBOTOMY
            </div>
            <h5 className="lis-task-title">Patient Flow & Queue Monitoring</h5>
            <p className="lis-task-desc">Guide registered patients to phlebotomy collection cubicle or radiology suite.</p>
            <span className="lis-task-link">View Collection Queue &rarr;</span>
          </div>
        </div>
      </div>

      {/* 3. Recent Patient Check-Ins (Real Live Data) */}
      <div className="lis-deck-section">
        <div className="lis-section-header-compact">
          <div className="lis-section-header-left">
            <span className="lis-section-kicker">PATIENT ENCOUNTERS</span>
            <h4 className="lis-section-title">Recent Shift Registrations</h4>
          </div>
          <button
            type="button"
            className="lis-deck-quick-btn"
            onClick={() => navigate('/registration')}
          >
            All Registrations &rarr;
          </button>
        </div>

        <div className="lis-table-card">
          {recent.length === 0 ? (
            <div className="lis-empty-state">
              <p>No recent registrations recorded for this center today.</p>
            </div>
          ) : (
            <div className="lis-table-responsive">
              <table className="lis-data-table">
                <thead>
                  <tr>
                    <th>Lab No</th>
                    <th>Patient Name</th>
                    <th>Age / Gender</th>
                    <th>Contact</th>
                    <th>Total Billed</th>
                    <th>Due Amount</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((item) => (
                    <tr key={item.ledgerTransactionId || item.id || item.labno || item.ledgerTransactionNo}>
                      <td>
                        <strong className="lis-labno-highlight">{item.ledgerTransactionNo || item.labNo || item.labno}</strong>
                      </td>
                      <td>{item.patientName || item.pname || 'Patient'}</td>
                      <td>{item.ageGender || `${item.age ? item.age + ' ' + (item.ageType || 'Y') : '-'} / ${item.gender || '-'}`}</td>
                      <td>{item.mobile || '-'}</td>
                      <td>₹{Number(item.netAmount ?? item.grossAmount ?? 0).toLocaleString('en-IN')}</td>
                      <td>
                        <span className={Number(item.balanceDue ?? item.dueAmount ?? 0) > 0 ? 'lis-badge-due' : 'lis-badge-paid'}>
                          ₹{Number(item.balanceDue ?? item.dueAmount ?? 0).toLocaleString('en-IN')}
                        </span>
                      </td>
                      <td>
                        <span className={`lis-status-pill ${item.isSampleCollected || item.isCollected ? 'collected' : 'pending'}`}>
                          {item.isSampleCollected || item.isCollected ? 'Collected' : 'Awaiting Draw'}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="lis-table-btn"
                          onClick={() => navigate('/settlement')}
                          title="Open billing settlement"
                        >
                          Bill
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReceptionCommandCenter;
